import { SideName } from "@tosize/shapes/src/lib/models/Side/SideName";
import type { AvailableOnSide, Finish } from "@tosize/shapes/src/types/finish";
import { formatNumber } from "@tosize/ui";
import { useMemo } from "react";
import type { IntlShape } from "react-intl";
import { usePostDataContext } from "../context/PostDataContext";
import useDimensionsForm from "./useDimensionsForm";
import useFinishes from "./useFinishes";
import useShapes from "./useShapes";

const useFinishSide = ({ side, intl }: { side?: AvailableOnSide; intl: IntlShape }) => {
	const { workpiece } = usePostDataContext();
	const { finishes } = useFinishes();
	const { shape } = useShapes();
	const { shapeModel } = useDimensionsForm(shape);

	const appliedFinish: Finish | undefined = useMemo(() => {
		const selectedSide = side ? workpiece.finishes.side[side.sideCode] : undefined;

		if (selectedSide !== undefined) {
			for (const code of Object.keys(selectedSide)) {
				return finishes?.find((finish) =>
					finish.availableOnSides.find((side) => side.finishCode === code),
				);
			}
		}

		return undefined;
	}, [workpiece.finishes, finishes, side]);

	const sideLength = side
		? shapeModel?.calculateSideLength(side.sideCode, workpiece.shape_dimensions)
		: undefined;
	const dimensionsString = shapeModel?.getDimensionsString(workpiece.shape_dimensions, intl);

	const sideLengthString = useMemo(() => {
		if (
			dimensionsString &&
			(side?.sideCode === SideName.Top || side?.sideCode === SideName.Bottom)
		) {
			return `(${dimensionsString})`;
		}

		return sideLength !== undefined
			? `(${formatNumber({
					lang: intl.locale,
					value: sideLength / 10,
					style: "unit",
					unit: "centimeter",
				})})`
			: undefined;
	}, [dimensionsString, intl, side?.sideCode, sideLength]);

	return {
		appliedFinish,
		sideLengthString,
	};
};

export default useFinishSide;
